import React, { useState } from 'react';
import { ArrowUpRight, Play, ChevronLeft, ChevronRight } from 'lucide-react';

interface Slide {
  src: string;
  tag: string;
  role: string;
  org: string;
  accent: string;
  position: string;
}

const SLIDES: Slide[] = [
  {
    src: '/images/thomas-suit.jpg',
    tag: 'Cloud Security Architecture',
    role: 'Strategic Cloud Engineer',
    org: 'Google Cloud',
    accent: 'var(--accent-gold)',
    position: 'center 15%',
  },
  {
    src: '/images/thomas-clarinet-azaleas.jpg',
    tag: 'Classical Performance',
    role: 'Clarinet & Saxophone Recitalist',
    org: 'Blair School of Music Alum',
    accent: 'var(--accent-emerald)',
    position: 'center 20%',
  },
  {
    src: '/images/thomas-clarinet-outdoor.jpg',
    tag: 'Festival Production',
    role: 'Director of Operations & Production',
    org: 'Classical Remix Music Festival',
    accent: 'var(--accent-sky)',
    position: 'center 25%',
  },
];

export const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? SLIDES.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === SLIDES.length - 1 ? 0 : prev + 1));
  };

  const activeSlide = SLIDES[currentSlide];

  return (
    <header className="hero-section">
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, 0.95fr)', gap: '3.5rem', alignItems: 'center' }} className="hero-grid">
          {/* Left Column: Headline & Bio */}
          <div>
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              <span>Google Cloud &bull; Classical Remix &bull; Blair Alum</span>
            </div>

            <h1 className="hero-title">
              Engineering Resilient Systems.<br />
              <span className="gradient-text">Performing with Precision.</span>
            </h1>

            <p className="hero-subtitle">
              I build enterprise cloud security and large-scale developer tools at <strong>Google Cloud</strong>, 
              lead operations and production for <strong>Classical Remix Music Festival</strong>, 
              and perform clarinet and tenor saxophone in Atlanta and beyond.
            </p>

            <div className="hero-actions">
              <a href="#work" className="btn btn-primary">
                <span>Explore Portfolio</span>
                <ArrowUpRight size={18} />
              </a>
              <a href="#recordings" className="btn btn-secondary">
                <Play size={16} fill="currentColor" />
                <span>Recordings & Performances</span>
              </a>
            </div>

            {/* Quick Metrics / Disciplines Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-subtle)' }}>
              <div>
                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--accent-gold)', fontFamily: 'var(--font-display)' }}>
                  Google Cloud
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Cloud Security & Architecture
                </div>
              </div>
              <div>
                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--accent-emerald)', fontFamily: 'var(--font-display)' }}>
                  Classical Remix
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Production & Operations
                </div>
              </div>
              <div>
                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--accent-sky)', fontFamily: 'var(--font-display)' }}>
                  Clarinet & Sax
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Chamber & Studio Artist
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Interactive Portrait Slideshow (Hidden on mobile) */}
          <div className="hero-portrait-card" style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: '380px',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                boxShadow: '0 20px 45px rgba(0, 0, 0, 0.45), 0 0 35px rgba(212, 175, 55, 0.08)',
              }}
            >
              {/* Photo Display Frame */}
              <div style={{ position: 'relative', width: '100%', paddingTop: '125%', overflow: 'hidden' }}>
                {SLIDES.map((slide, index) => (
                  <img
                    key={slide.src}
                    src={slide.src}
                    alt={`Thomas Desrosiers — ${slide.tag}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: slide.position,
                      opacity: index === currentSlide ? 1 : 0,
                      transform: index === currentSlide ? 'scale(1)' : 'scale(1.04)',
                      transition: 'opacity 0.45s ease-in-out, transform 0.45s ease-in-out',
                      pointerEvents: index === currentSlide ? 'auto' : 'none',
                    }}
                  />
                ))}

                {/* Subtle vignette gradient overlay */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to top, rgba(10, 13, 20, 0.88) 0%, rgba(10, 13, 20, 0.15) 45%, transparent 100%)',
                    pointerEvents: 'none',
                  }}
                />

                {/* Top Badge: Slide Category Tag */}
                <div
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    left: '1rem',
                    background: 'rgba(10, 13, 20, 0.75)',
                    backdropFilter: 'blur(12px)',
                    padding: '0.3rem 0.75rem',
                    borderRadius: 'var(--radius-full)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: activeSlide.accent,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                  }}
                >
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: activeSlide.accent }} />
                  <span>{activeSlide.tag}</span>
                </div>

                {/* Left & Right Arrow Navigation Controls */}
                <button
                  type="button"
                  onClick={prevSlide}
                  aria-label="Previous headshot"
                  style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '2rem',
                    height: '2rem',
                    borderRadius: '50%',
                    background: 'rgba(10, 13, 20, 0.65)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 3,
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.85)'; e.currentTarget.style.color = '#000'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(10, 13, 20, 0.65)'; e.currentTarget.style.color = '#fff'; }}
                >
                  <ChevronLeft size={16} />
                </button>

                <button
                  type="button"
                  onClick={nextSlide}
                  aria-label="Next headshot"
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '2rem',
                    height: '2rem',
                    borderRadius: '50%',
                    background: 'rgba(10, 13, 20, 0.65)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 3,
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.85)'; e.currentTarget.style.color = '#000'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(10, 13, 20, 0.65)'; e.currentTarget.style.color = '#fff'; }}
                >
                  <ChevronRight size={16} />
                </button>

                {/* Bottom Card Details & Navigation Dots */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '1rem',
                    left: '1.25rem',
                    right: '1.25rem',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#fff', fontFamily: 'var(--font-display)' }}>
                      Thomas Desrosiers
                    </div>
                    <div style={{ fontSize: '0.8rem', color: activeSlide.accent, marginTop: '0.15rem' }}>
                      {activeSlide.role} &bull; {activeSlide.org}
                    </div>
                  </div>

                  {/* Interactive Indicator Dots */}
                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                    {SLIDES.map((_, dotIndex) => (
                      <button
                        key={dotIndex}
                        type="button"
                        onClick={() => setCurrentSlide(dotIndex)}
                        aria-label={`Go to slide ${dotIndex + 1}`}
                        style={{
                          height: '6px',
                          width: dotIndex === currentSlide ? '20px' : '6px',
                          borderRadius: '3px',
                          background: dotIndex === currentSlide ? activeSlide.accent : 'rgba(255, 255, 255, 0.35)',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative background glow */}
            <div
              style={{
                position: 'absolute',
                inset: '-20px',
                background: 'radial-gradient(circle, rgba(212, 175, 55, 0.12) 0%, transparent 70%)',
                zIndex: -1,
                pointerEvents: 'none',
              }}
            />
          </div>
        </div>
      </div>
    </header>
  );
};
